import { matchPath } from 'react-router-dom';

export type ParentEntry = {
  pattern: string;
  parent: string;
  label: string;
};

/**
 * Routes considered top-level: these never show a back bar.
 * The bar also stays hidden for any route not present in the registry below.
 */
export const TOP_LEVEL_ROUTES = [
  '/',
  '/projects',
  '/estimates',
  '/orders',
  '/inventory',
  '/employees',
  '/calendar',
  '/docs',
  '/pipeline',
  '/tasks',
  '/contacts',
  '/relationships',
  '/receipts',
  '/billing',
  '/login',
] as const;

/**
 * Single source of truth for non-top-level routes. Order matters only for
 * specificity: more specific patterns must precede more generic ones so the
 * `matchPath` walk finds the deepest match first.
 */
export const PARENT_REGISTRY: ParentEntry[] = [
  // Projects subtree
  { pattern: '/projects/:projectId/orders', parent: '/projects/:projectId', label: 'Project' },
  { pattern: '/projects/:projectId/receipts', parent: '/projects/:projectId', label: 'Project' },
  { pattern: '/projects/:projectId/estimates', parent: '/projects/:projectId', label: 'Project' },
  { pattern: '/projects/:projectId/apps/new', parent: '/projects/:projectId/apps', label: 'Apps' },
  { pattern: '/projects/:projectId/apps', parent: '/projects/:projectId', label: 'Project' },
  { pattern: '/projects/:projectId/floor-plans/new', parent: '/projects/:projectId', label: 'Project' },
  { pattern: '/projects/:id/details', parent: '/projects/:id', label: 'Project' },
  { pattern: '/projects/:id/actuals', parent: '/projects/:id', label: 'Project' },
  { pattern: '/projects/:id/pnl', parent: '/projects/:id', label: 'Project' },
  { pattern: '/projects/:id/edit', parent: '/projects/:id', label: 'Project' },
  { pattern: '/projects/create', parent: '/projects', label: 'Projects' },
  { pattern: '/projects/:id', parent: '/projects', label: 'Projects' },

  // Standalone deep entities — registry default is the closest known list.
  // Pages override these once the entity loads with the precise project link.
  { pattern: '/project-payment-apps/:id/edit', parent: '/projects', label: 'Projects' },
  { pattern: '/project-payment-apps/:id', parent: '/projects', label: 'Projects' },
  { pattern: '/floor-plans/:id', parent: '/projects', label: 'Projects' },
  { pattern: '/lab-sample-results/:id', parent: '/projects', label: 'Projects' },
  { pattern: '/inspections/new', parent: '/projects', label: 'Projects' },
  { pattern: '/inspections/:id/edit', parent: '/projects', label: 'Projects' },
  { pattern: '/inspections/:id', parent: '/projects', label: 'Projects' },

  // Estimates
  { pattern: '/estimates/new', parent: '/estimates', label: 'Estimates' },
  { pattern: '/estimates/:id/edit', parent: '/estimates/:id', label: 'Estimate' },
  { pattern: '/estimates/:id', parent: '/estimates', label: 'Estimates' },

  // Orders
  { pattern: '/orders/new', parent: '/orders', label: 'Orders' },
  { pattern: '/orders/:id/edit', parent: '/orders/:id', label: 'Order' },
  { pattern: '/orders/:id', parent: '/orders', label: 'Orders' },

  // Employees
  { pattern: '/employees/workers/:id', parent: '/employees', label: 'Employees' },

  // Calendar
  { pattern: '/calendar/assignments/:id', parent: '/calendar', label: 'Calendar' },

  // CRM
  { pattern: '/deals/:id', parent: '/pipeline', label: 'Pipeline' },
  { pattern: '/contacts/:id', parent: '/contacts', label: 'Contacts' },
  { pattern: '/companies/:id', parent: '/relationships', label: 'Relationships' },
];

/**
 * Pattern → label map used to describe an arbitrary origin route ("Back to X"
 * where X is the page the user navigated from). More specific patterns first.
 */
const ROUTE_LABELS: { pattern: string; label: string }[] = [
  // Projects subtree
  { pattern: '/projects/:projectId/receipts/batches/:batchId/review', label: 'Receipt Review' },
  { pattern: '/projects/:projectId/receipts/bulk', label: 'Receipt Upload' },
  { pattern: '/projects/:projectId/receipts', label: 'Project Receipts' },
  { pattern: '/projects/:projectId/orders', label: 'Project Orders' },
  { pattern: '/projects/:projectId/estimates', label: 'Project Estimates' },
  { pattern: '/projects/:projectId/apps/new', label: 'New Payment App' },
  { pattern: '/projects/:projectId/apps', label: 'Payment Apps' },
  { pattern: '/projects/:projectId/floor-plans/new', label: 'Floor Plan' },
  { pattern: '/projects/:id/details', label: 'Project Details' },
  { pattern: '/projects/:id/actuals', label: 'Project Actuals' },
  { pattern: '/projects/:id/pnl', label: 'Project P&L' },
  { pattern: '/projects/:id/edit', label: 'Project' },
  { pattern: '/projects/create', label: 'New Project' },
  { pattern: '/projects/:id', label: 'Project' },
  { pattern: '/projects', label: 'Projects' },

  // Standalone deep entities
  { pattern: '/project-payment-apps/:id/edit', label: 'Payment App' },
  { pattern: '/project-payment-apps/:id', label: 'Payment App' },
  { pattern: '/floor-plans/:id', label: 'Floor Plan' },
  { pattern: '/lab-sample-results/:id', label: 'Lab Results' },
  { pattern: '/inspections/new', label: 'New Inspection' },
  { pattern: '/inspections/:id/edit', label: 'Inspection' },
  { pattern: '/inspections/:id', label: 'Inspection' },

  // Estimates
  { pattern: '/estimates/new', label: 'New Estimate' },
  { pattern: '/estimates/:id/edit', label: 'Estimate' },
  { pattern: '/estimates/:id', label: 'Estimate' },
  { pattern: '/estimates', label: 'Estimates' },

  // Orders
  { pattern: '/orders/new', label: 'New Order' },
  { pattern: '/orders/:id/edit', label: 'Order' },
  { pattern: '/orders/:id', label: 'Order' },
  { pattern: '/orders', label: 'Orders' },

  // Receipts
  { pattern: '/receipts/new', label: 'New Receipt' },
  { pattern: '/receipts/:id/edit', label: 'Receipt' },
  { pattern: '/receipts/:id', label: 'Receipt' },
  { pattern: '/receipts', label: 'Receipts' },

  // Employees / Calendar
  { pattern: '/employees/workers/:id', label: 'Worker' },
  { pattern: '/employees', label: 'Employees' },
  { pattern: '/calendar/assignments/:id', label: 'Assignment' },
  { pattern: '/calendar', label: 'Calendar' },

  // CRM
  { pattern: '/deals/:id', label: 'Deal' },
  { pattern: '/contacts/:id', label: 'Contact' },
  { pattern: '/companies/:id', label: 'Company' },
  { pattern: '/pipeline', label: 'Pipeline' },
  { pattern: '/contacts', label: 'Contacts' },
  { pattern: '/relationships', label: 'Relationships' },

  // Other top-level
  { pattern: '/billing', label: 'Billing' },
  { pattern: '/inventory', label: 'Inventory' },
  { pattern: '/tasks', label: 'Tasks' },
  { pattern: '/docs', label: 'Docs' },
];

/**
 * Human label for an arbitrary in-app path (query string is ignored).
 * Returns null for unknown routes — the BackBar renders a plain "Back".
 */
export function routeLabel(path: string): string | null {
  const pathname = path.split('?')[0];
  for (const entry of ROUTE_LABELS) {
    if (matchPath({ path: entry.pattern, end: true }, pathname)) return entry.label;
  }
  return null;
}

export function isTopLevelRoute(pathname: string): boolean {
  return (TOP_LEVEL_ROUTES as readonly string[]).includes(pathname);
}

export type ResolvedParent = {
  to: string;
  label: string;
} | null;

/**
 * Walk the registry, return the first match. Substitutes any params from the
 * matched route into the parent pattern, so `:id` etc. flow through.
 */
export function resolveParent(pathname: string): ResolvedParent {
  if ((TOP_LEVEL_ROUTES as readonly string[]).includes(pathname)) return null;

  for (const entry of PARENT_REGISTRY) {
    const match = matchPath({ path: entry.pattern, end: true }, pathname);
    if (!match) continue;
    const params = (match.params ?? {}) as Record<string, string | undefined>;
    const to = entry.parent.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
      const value = params[key];
      return value ?? `:${key}`;
    });
    return { to, label: entry.label };
  }
  return null;
}
